/**
 * /learn/courses/:assignmentId/sections/:sectionId/modules/:moduleId — single-
 * module view. Also reachable via the admin preview surface
 * (/courses/:courseId/preview/...) — the page accepts either route param.
 *
 * Hosts the appropriate view from `components/modules/views/registry` via
 * the shared ModulePlayer. Evaluation / feedback modules redirect to the
 * existing dedicated routes — they don't render inline here.
 *
 * Responsibilities:
 *   - **Linear gate**: when the URL `moduleId` is past the cursor (the first
 *     in_progress / not_started module across the whole course), redirect to
 *     the cursor. Course bucket = 'completed' bypasses the gate (review).
 *   - **Mark complete & next**: content-type modules POST /complete and
 *     advance — within a section first, then bouncing to the next section's
 *     first module, then to /done.
 *   - **Close-on-leave beacon**: registers a `pagehide` listener that hits
 *     `POST /me/courses/:assignmentId/leave` via `navigator.sendBeacon`.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { slideUp } from '@/lib/motion';
import { useCourse } from '../../hooks/learner/useCourse';
import { useMarkModuleComplete } from '../../hooks/learner/useMarkModuleComplete';
import { useCourseRoutes } from '../../hooks/learner/useCourseRoutes';
import { useModuleTimer } from '../../hooks/learner/useModuleTimer';
import { leaveBeaconUrl } from '../../api/me';
import ModulePlayer from '../../components/learner/ModulePlayer';
import LearnerModuleHeader from '../../components/learner/LearnerModuleHeader';
import { getEnrolmentAvailability, isEnrolmentLocked } from '../../utils/enrolmentAvailability';
import type { components } from '../../types/learnerApi';

type Section = components['schemas']['Section'];
type ContentModule = components['schemas']['ContentModule'];
type EvaluationModule = components['schemas']['EvaluationModule'];
type FeedbackModule = components['schemas']['FeedbackModule'];
type AnyModule = ContentModule | EvaluationModule | FeedbackModule;

interface FlatModule {
  module: AnyModule;
  section: Section;
}

function moduleUrl(base: string, sectionId: string, moduleId: string) {
  return `${base}/sections/${sectionId}/modules/${moduleId}`;
}

function moduleStatus(m: AnyModule): 'not_started' | 'in_progress' | 'completed' | undefined {
  return (m as ContentModule).progress?.status as
    | 'not_started'
    | 'in_progress'
    | 'completed'
    | undefined;
}

const CourseModulePage: React.FC = () => {
  const { assignmentId, moduleId } = useParams<{
    assignmentId?: string;
    sectionId: string;
    moduleId: string;
  }>();
  const id = assignmentId ?? '';
  const navigate = useNavigate();
  const routes = useCourseRoutes(id);
  const { data: course, isLoading, error } = useCourse(id);
  const markComplete = useMarkModuleComplete(id);

  // Flatten sections → modules in display order.
  const flat: FlatModule[] = useMemo(() => {
    if (!course?.sections) return [];
    const out: FlatModule[] = [];
    course.sections
      .slice()
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
      .forEach((section) => {
        (section.modules ?? [])
          .slice()
          .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
          .forEach((module) => {
            out.push({ module: module as AnyModule, section });
          });
      });
    return out;
  }, [course]);

  const currentIdx = flat.findIndex((f) => f.module.id === moduleId);
  const current = currentIdx >= 0 ? flat[currentIdx] : null;
  const next = currentIdx >= 0 && currentIdx < flat.length - 1 ? flat[currentIdx + 1] : null;

  const courseCompleted = course?.progress?.bucket === 'completed';

  // Date-window gate: bounce deep-links back to the detail page when the
  // enrolment hasn't opened yet or has already ended. Completed enrolments
  // bypass the end_date gate (review mode stays open).
  useEffect(() => {
    if (!course) return;
    const availability = getEnrolmentAvailability(course.assignment, {
      completed: courseCompleted,
    });
    if (isEnrolmentLocked(availability)) {
      navigate(routes.base, { replace: true });
    }
  }, [course, courseCompleted, navigate, routes.base]);

  // Cursor: first in_progress module index, else first not_started.
  const cursorIdx = useMemo(() => {
    let firstNotStarted = -1;
    for (let i = 0; i < flat.length; i++) {
      const status = moduleStatus(flat[i].module);
      if (status === 'in_progress') return i;
      if (firstNotStarted < 0 && (!status || status === 'not_started')) {
        firstNotStarted = i;
      }
    }
    return firstNotStarted;
  }, [flat]);

  // Linear gate: redirect when the URL is past the cursor (and course isn't
  // in review mode). Lets review-mode browsing free-roam.
  useEffect(() => {
    if (isLoading || !id) return;
    if (courseCompleted) return;
    if (currentIdx < 0) return;
    if (cursorIdx < 0) return;
    if (currentIdx > cursorIdx) {
      const target = flat[cursorIdx];
      if (target?.module.id && target.section.id) {
        navigate(moduleUrl(routes.base, target.section.id, target.module.id), { replace: true });
      }
    }
  }, [isLoading, id, courseCompleted, currentIdx, cursorIdx, flat, navigate, routes.base]);

  // Evaluation / feedback redirect to dedicated routes (nested under the
  // section so URLs stay consistent).
  useEffect(() => {
    if (!current || !id) return;
    if (!current.section.id || !current.module.id) return;
    if (current.module.type === 'evaluation') {
      navigate(
        `${moduleUrl(routes.base, current.section.id, current.module.id)}/evaluate`,
        { replace: true }
      );
    } else if (current.module.type === 'feedback') {
      navigate(
        `${moduleUrl(routes.base, current.section.id, current.module.id)}/feedback`,
        { replace: true }
      );
    }
  }, [current, id, navigate, routes.base]);

  // Close-on-leave beacon — fires from pagehide and tab-hide.
  useEffect(() => {
    if (!id) return;
    const url = leaveBeaconUrl(id);
    const blob = new Blob([], { type: 'application/json' });
    const send = () => {
      try {
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          navigator.sendBeacon(url, blob);
        }
      } catch {
        // best-effort — server-side timeouts will catch any miss
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') send();
    };
    window.addEventListener('pagehide', send);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', send);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [id]);

  // Track whether mark-complete fired so the button can show a settle state.
  const markPendingRef = useRef(false);

  // Track dwell time per module and flush it on navigation / tab-close. Only
  // content modules are timed here — evaluation/feedback redirect to their own
  // player pages (see the effect above), which run their own timer. Timing is
  // decoupled from completion so leave-without-complete time is no longer lost.
  const timedModuleId =
    current && current.module.type !== 'evaluation' && current.module.type !== 'feedback'
      ? current.module.id
      : undefined;
  const moduleTimer = useModuleTimer(id, timedModuleId);

  const advance = useCallback(() => {
    if (!id) return;
    if (next?.module.id && next.section.id) {
      navigate(moduleUrl(routes.base, next.section.id, next.module.id));
    } else {
      navigate(`${routes.base}/done`);
    }
  }, [id, next, navigate, routes.base]);

  const handleMarkComplete = useCallback(async () => {
    if (!current?.module.id || !id) return;
    if (markPendingRef.current) return;
    markPendingRef.current = true;
    try {
      const status = moduleStatus(current.module);
      if (status !== 'completed') {
        // Dwell time is recorded by useModuleTimer on navigation, not here, so
        // a module's time is never double-counted.
        // Send the dwell with the completion so the server can anchor the
        // module's started_at at the true opening moment; taking it resets
        // the timer, so the unmount flush only reports the remainder.
        await markComplete.mutateAsync({
          moduleId: current.module.id,
          durationSeconds: moduleTimer.takeElapsed(),
        });
      }
      advance();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Could not mark this module complete. Please try again.';
      toast.error(message);
    } finally {
      // Always reset so a transient failure doesn't lock the button forever.
      markPendingRef.current = false;
    }
  }, [current, id, markComplete, advance]);

  // SCORM packages announce completion from inside their sandboxed player;
  // route it through the exact same flow as the Mark-complete button.
  useEffect(() => {
    const onScorm = (e: MessageEvent) => {
      const d = e.data;
      if (d && d.type === 'tereiko:scorm' && (d.status === 'completed' || d.status === 'passed')) {
        void handleMarkComplete();
      }
    };
    window.addEventListener('message', onScorm);
    return () => window.removeEventListener('message', onScorm);
  }, [handleMarkComplete]);

  if (isLoading) return <ModulePageSkeleton />;

  if (error || !course) {
    return (
      <div className="w-[90%] sm:w-[80%] mx-auto py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't load this course.</p>
        <Link to={routes.coursesIndex} className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to My Courses
        </Link>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="w-[90%] sm:w-[80%] mx-auto py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't find that module.</p>
        <Link
          to={`${routes.base}/sections`}
          className="text-sm font-bold text-primary-500 hover:underline"
        >
          ← Back to sections
        </Link>
      </div>
    );
  }

  // While the redirect effect runs for evaluation / feedback / linear-gate,
  // render nothing so we don't flash content the user shouldn't see yet.
  if (
    current.module.type === 'evaluation' ||
    current.module.type === 'feedback' ||
    (!courseCompleted && cursorIdx >= 0 && currentIdx > cursorIdx)
  ) {
    return null;
  }

  const isLast = next === null;
  const ctaLabel = courseCompleted
    ? isLast
      ? 'Back to overview'
      : 'Next'
    : isLast
      ? 'Finish course'
      : 'Next';

  const handleCtaClick = () => {
    if (courseCompleted) {
      // Review mode — just navigate, don't write progress.
      if (isLast) {
        navigate(routes.base);
      } else if (next?.module.id && next.section.id) {
        navigate(moduleUrl(routes.base, next.section.id, next.module.id));
      }
      return;
    }
    handleMarkComplete();
  };

  const sectionLink = current.section.id
    ? `${routes.base}/sections/${current.section.id}/modules`
    : `${routes.base}/sections`;

  const completedCount = flat.filter((f) => moduleStatus(f.module) === 'completed').length;
  const percent = flat.length > 0 ? (completedCount / flat.length) * 100 : 0;

  return (
    <motion.div
      {...slideUp}
      className="w-[95%] sm:w-[90%] mx-auto py-6 flex-1 flex flex-col gap-6 min-h-0"
    >
      <LearnerModuleHeader
        backTo={sectionLink}
        percent={percent}
        sectionTitle={current.section.title || 'Section'}
        next={{
          label: ctaLabel,
          onClick: handleCtaClick,
          loading: markComplete.isPending,
          finish: isLast && !courseCompleted,
        }}
      />

      {/* Audit #12: the journey stays visible — what's next and how much
          is left, so the flow self-propels without going back up a level. */}
      {next ? (
        <p className="px-1 pb-2 text-xs text-text-secondary" data-testid="up-next-strip">
          Up next:{' '}
          <span className="font-semibold text-text-primary">
            {next.module.title || 'Next module'}
          </span>
          {' · '}
          {flat.length - 1 - currentIdx} module
          {flat.length - 1 - currentIdx === 1 ? '' : 's'} to go
        </p>
      ) : (
        <p className="px-1 pb-2 text-xs text-text-secondary" data-testid="up-next-strip">
          This is the last module — finish it to complete the course! 🎉
        </p>
      )}

      <div className="flex-1 flex flex-col min-h-0 pb-2">
        <ModulePlayer module={current.module as ContentModule} />
      </div>
    </motion.div>
  );
};

export default CourseModulePage;

// ─── Sub-components ──────────────────────────────────────────────────────────

function ModulePageSkeleton() {
  return (
    <div className="w-[90%] sm:w-[80%] mx-auto py-6 space-y-6 animate-pulse">
      <div className="h-4 w-32 rounded bg-text-tertiary/10" />
      <div className="h-3 w-1/3 rounded bg-text-tertiary/10" />
      <div className="h-8 w-2/3 rounded bg-text-tertiary/10" />
      <div className="h-2 w-1/2 rounded bg-text-tertiary/10" />
      <div className="h-64 rounded-2xl bg-text-tertiary/10" />
    </div>
  );
}
